import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { KeyValue, Pill, Spinner, LineTable } from '../../components/ui';
import { useDetail } from '../../lib/useDetail';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, money, num, paymentMethodLabel, paymentStatus } from '../../lib/format';
import type { Payment, PaymentAllocation } from '../../lib/types';
import { AttachmentPanel } from './AttachmentPanel';
import { FinanceGateNotice, useFinanceGate } from './FinanceGate';

// 收款页面（I03 只读基线）。
//
// 此前 core-api 只有 POST /api/payments：收款能建、不能查。本页补上查询与核销明细。
//
// 刻意不提供（未定口径，不实现）：
//   · 收款撤销 / 冲销（含逐笔与批量）：涉及余额重算与会计口径；
//   · 电子支票到账判定：`method` 目前是自由文本，「已登记收款」不等于「已到账」；
//   · 对账批量核销与部分开票判定。
// 页面只陈述已登记事实，不推断到账与结清结论。
function AllocationTable({ allocations }: { allocations: PaymentAllocation[] }) {
  return (
    <LineTable<PaymentAllocation>
      columns={[
        { header: '发票编号', render: (a) => a.internal_number || a.invoice_id },
        { header: '票种', render: (a) => a.voucher_type || '—' },
        { header: '销售点·凭证号', render: (a) => `${a.point_of_sale || '—'}·${a.voucher_number ?? '—'}` },
        { header: '开票日期', render: (a) => dateLabel(a.invoice_date) },
        { header: '本次核销额', align: 'right', render: (a) => money(a.amount, a.currency || 'ARS') },
        { header: '发票 ARS 总额', align: 'right', render: (a) => money(a.grand_total_ars, 'ARS') },
        { header: '该票当前未收', align: 'right', render: (a) => money(a.invoice_outstanding_amount, 'ARS') },
      ]}
      rows={allocations}
      emptyText="该收款未核销任何发票"
    />
  );
}

function PaymentDetail({ row }: { row: Payment }) {
  const { user } = useAuth();
  const { data, loading, error } = useDetail<Payment>(`/api/payments/${row.id}`);
  const payment = data ?? row;
  const st = paymentStatus(payment.status);
  const allocations = payment.allocations || [];

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{payment.number}</span>
        <h3>{payment.customer_name || payment.customer_id}</h3>
        <Pill tone={st.tone} label={st.label} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}

      <div className="kv-grid">
        <KeyValue k="收款日期">{dateLabel(payment.payment_date)}</KeyValue>
        <KeyValue k="客户编码">{payment.customer_code || '—'}</KeyValue>
        <KeyValue k="币种">{payment.currency}</KeyValue>
        <KeyValue k="金额（原币）">{money(payment.amount, payment.currency)}</KeyValue>
        <KeyValue k="汇率">{num(payment.exchange_rate, 6)}</KeyValue>
        <KeyValue k="ARS 折算（推算）">{money(payment.amount_ars_equivalent, 'ARS')}</KeyValue>
        <KeyValue k="收款方式">{paymentMethodLabel(payment.method)}</KeyValue>
        <KeyValue k="参考号">{payment.reference || '—'}</KeyValue>
        <KeyValue k="已核销">{money(payment.allocated_amount, payment.currency)}</KeyValue>
        <KeyValue k="未核销">{money(payment.unallocated_amount, payment.currency)}</KeyValue>
        <KeyValue k="来源">{`${payment.source_system || '—'} · ${payment.source_id || '—'}`}</KeyValue>
        <KeyValue k="备注">{payment.notes || '—'}</KeyValue>
      </div>

      <div className="muted" style={{ fontSize: 12.5, margin: '6px 0 12px' }}>
        「本次核销额」是该收款登记时分配到发票的金额；「该票当前未收」会随后续收款变小，
        两者不是同一个数。ARS 折算为展示用推算（原币 × 汇率），原币与 ARS 分别落库尚未实现。
        收款撤销/冲销与电子支票到账判定均未实现，本页不显示结清结论。
      </div>

      <div style={{ fontWeight: 650, marginBottom: 6 }}>核销明细</div>
      <div style={{ marginBottom: 12 }}><AllocationTable allocations={allocations} /></div>

      <div style={{ fontWeight: 650, marginBottom: 6 }}>附件（回单等原件）</div>
      <AttachmentPanel entityType="payment" entityId={payment.id} canUpload={can(user, 'attachments', 'create')} />
    </div>
  );
}
/**
 * 页面入口。
 *
 * 门控状态统一走 useFinanceGate()（与导航、税票页同源），**不再用本页端点自己探**：
 * 门控关闭时 GET /api/payments 是 503，但 GET /api/arca-invoices 为了向后兼容仍按旧信封
 * 返回 200 —— 拿后者探会把「本环境未启用」误判成「已启用」。
 */
export function PaymentsPage() {
  const gate = useFinanceGate();
  if (gate === 'disabled') return <FinanceGateNotice />;
  if (gate !== 'enabled') return <Spinner label="正在确认本环境财务能力…" />;

  const columns: Column<Payment>[] = [
    { header: '收款编号', render: (r) => <span className="num">{r.number}</span> },
    { header: '收款日期', render: (r) => dateLabel(r.payment_date) },
    { header: '客户', render: (r) => r.customer_name || r.customer_id },
    { header: '金额', align: 'right', render: (r) => money(r.amount, r.currency) },
    { header: '汇率', align: 'right', render: (r) => num(r.exchange_rate, 4) },
    { header: 'ARS 折算', align: 'right', render: (r) => money(r.amount_ars_equivalent, 'ARS') },
    { header: '方式', render: (r) => paymentMethodLabel(r.method) },
    { header: '参考号', render: (r) => r.reference || '—' },
    { header: '已核销', align: 'right', render: (r) => money(r.allocated_amount, r.currency) },
    { header: '未核销', align: 'right', render: (r) => money(r.unallocated_amount, r.currency) },
    { header: '状态', render: (r) => { const s = paymentStatus(r.status); return <Pill tone={s.tone} label={s.label} />; } },
  ];

  return (
    <ListPage<Payment>
      title="收款"
      desc="收款登记与核销明细查询（只读基线）。收款方式的到账状态尚未落库：已登记 ≠ 已到账。"
      endpoint="/api/payments"
      rowId={(r) => r.id}
      columns={columns}
      renderDetail={(row) => <PaymentDetail row={row} />}
      searchPlaceholder="搜索收款编号 / 参考号 / 客户…"
    />
  );
}